import { useEffect, useState } from 'react'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import type { AdminWithdrawalRow } from '../../services/withdrawalRequestsService'

export const WITHDRAWAL_ADMIN_NOTE_MAX = 2000

const textareaClass =
  'mt-1.5 w-full min-h-[7rem] resize-y rounded-xl border border-border bg-card px-3 py-2.5 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60'

type WithdrawalActionKind = 'approve' | 'disburse' | 'reject'

type WithdrawalActionModalProps = {
  open: boolean
  kind: WithdrawalActionKind | null
  row: AdminWithdrawalRow | null
  submitting: boolean
  error?: string | null
  onClose: () => void
  onConfirm: (note: string) => void
}

function modalCopy(
  kind: WithdrawalActionKind,
  opts?: {
    stripeConnectReady?: boolean
    paystackRecipientReady?: boolean
    transferReady?: boolean | null
    nextReadyAt?: string | null
    transferableAmount?: string | null
    currency?: string
  },
) {
  if (kind === 'approve') {
    return {
      title: 'Approve withdrawal',
      description: 'Optional internal note for your team (not shown to the merchant).',
      confirm: 'Approve',
      submitting: 'Approving…',
      noteLabel: 'Internal note',
      noteOptional: true,
      notePlaceholder: 'e.g. Verified account name matches KYC; batch ref TX-2044',
    }
  }
  if (kind === 'reject') {
    return {
      title: 'Reject withdrawal',
      description:
        'The merchant will see this reason. Funds return to their available balance immediately.',
      confirm: 'Reject request',
      submitting: 'Rejecting…',
      noteLabel: 'Reason for rejection',
      noteOptional: false,
      notePlaceholder: 'e.g. Bank account name does not match organisation records',
    }
  }
  if (opts?.stripeConnectReady) {
    const notReady = opts.transferReady === false
    const when = opts.nextReadyAt
      ? ` Next funds may be ready around ${new Date(opts.nextReadyAt).toLocaleString()}.`
      : ''
    const settledHint =
      opts.transferableAmount != null
        ? ` Settled transferable now: ${opts.currency ?? ''} ${opts.transferableAmount}.`
        : ''
    return {
      title: notReady ? 'Transfer not ready yet' : 'Mark disbursed (Stripe Transfer)',
      description: notReady
        ? `Stripe charge funds are still pending settlement.${settledHint}${when} Retry Mark disbursed after funds become available — do not treat this as a failed bank wire.`
        : 'This store uses Stripe Connect. Confirming creates a Stripe Transfer to their Express account (source_transaction) and debits the wallet. No off-platform bank wire.',
      confirm: notReady ? 'Try transfer anyway' : 'Transfer & mark disbursed',
      submitting: 'Transferring…',
      noteLabel: 'Internal note',
      noteOptional: true,
      notePlaceholder: 'e.g. Manual release after support ticket #…',
    }
  }
  if (opts?.paystackRecipientReady) {
    return {
      title: 'Mark disbursed (Paystack Transfer)',
      description:
        'This store has a Paystack payout bank account. Confirming creates a Paystack Transfer from the platform balance and debits the wallet. No off-platform bank wire.',
      confirm: 'Transfer & mark disbursed',
      submitting: 'Transferring…',
      noteLabel: 'Internal note',
      noteOptional: true,
      notePlaceholder: 'e.g. Manual release after support ticket #…',
    }
  }
  return {
    title: 'Mark disbursed',
    description: 'Confirm the bank transfer is complete. Add an optional internal note for the audit trail.',
    confirm: 'Mark disbursed',
    submitting: 'Saving…',
    noteLabel: 'Internal note',
    noteOptional: true,
    notePlaceholder: 'e.g. Paid via batch TX-991',
  }
}

export function WithdrawalActionModal({
  open,
  kind,
  row,
  submitting,
  error,
  onClose,
  onConfirm,
}: WithdrawalActionModalProps) {
  const [note, setNote] = useState('')

  useEffect(() => {
    if (open) setNote('')
  }, [open, kind, row?.id])

  if (!kind || !row) return null

  const copy = modalCopy(kind, {
    stripeConnectReady: Boolean(row.stripeConnectReady),
    paystackRecipientReady: Boolean(row.paystackRecipientReady),
    transferReady: row.transferReady,
    nextReadyAt: row.nextReadyAt,
    transferableAmount: row.transferableAmount,
    currency: row.currency,
  })
  const trimmed = note.trim()
  const canConfirm = copy.noteOptional ? true : trimmed.length > 0

  return (
    <Modal
      open={open}
      onClose={() => {
        if (submitting) return
        onClose()
      }}
      title={copy.title}
      size="md"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" disabled={submitting} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={kind === 'reject' ? 'destructive' : 'default'}
            disabled={submitting || !canConfirm}
            onClick={() => onConfirm(note)}
          >
            {submitting ? copy.submitting : copy.confirm}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {row.organisationName ?? `Organisation #${row.organisationId}`} ·{' '}
          {row.storeName ?? `Store #${row.storeId}`} ·{' '}
          <span className="font-medium text-foreground">
            {row.currency} {row.amount}
          </span>
        </p>
        <p className="text-sm text-muted-foreground">{copy.description}</p>
        {error ? (
          <p
            className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
            role="alert"
          >
            {error}
          </p>
        ) : null}
        <div>
          <label htmlFor="withdrawal-action-note" className="text-xs font-medium text-muted-foreground">
            {copy.noteLabel}{' '}
            {copy.noteOptional ? <span className="font-normal">(optional)</span> : null}
          </label>
          <textarea
            id="withdrawal-action-note"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, WITHDRAWAL_ADMIN_NOTE_MAX))}
            maxLength={WITHDRAWAL_ADMIN_NOTE_MAX}
            disabled={submitting}
            placeholder={copy.notePlaceholder}
            className={textareaClass}
            aria-required={!copy.noteOptional}
          />
          <p className="mt-1 text-right text-[11px] tabular-nums text-muted-foreground">
            {note.length}/{WITHDRAWAL_ADMIN_NOTE_MAX}
          </p>
        </div>
      </div>
    </Modal>
  )
}
